import { createContext, useCallback, useContext, useRef, useState } from "react";
import * as AlertDialog from "@radix-ui/react-alert-dialog";
import { extras, light, fontStack } from "styles/tokens";
import "./ConfirmDialog.css";

/**
 * App-wide replacement for the native, unstyled `window.confirm()`.
 *
 * Hand-rolled on Radix AlertDialog (headless) — no MUI. Radix gives us the
 * focus trap, ESC handling, scroll-lock, focus restoration, portal and ARIA
 * (role="alertdialog"); this file owns only look + behavior. Styling is driven
 * by the shared design tokens (theme.js stays the single source of truth).
 *
 * Usage:
 *   const confirm = useConfirm();
 *   const ok = await confirm({ title, message, danger, confirmText });
 *   if (!ok) return;
 *
 * The hook returns a promise<boolean>, so each old
 *   `if (!window.confirm(...)) return;`
 * becomes a one-line `await`.
 */

const ConfirmContext = createContext(null);

const DEFAULTS = {
  title: "Are you sure?",
  message: "",
  confirmText: "Confirm",
  cancelText: "Cancel",
  danger: false,
};

// Token values injected as CSS custom properties (see ConfirmDialog.css).
const CSS_VARS = {
  "--cd-overlay": light.overlay,
  "--cd-surface": light.surface,
  "--cd-text-high": light.textHigh,
  "--cd-text-mid": light.textMid,
  "--cd-border": light.border,
  "--cd-border-strong": light.borderStrong,
  "--cd-radius": `${extras.radius.lg}px`,
  "--cd-radius-btn": `${extras.radius.sm}px`,
  "--cd-shadow": extras.shadow.lg,
  "--cd-font": fontStack,
  "--cd-accent": extras.accent.main,
  "--cd-accent-hover": extras.accent.hover,
  "--cd-accent-contrast": extras.accent.contrastText,
  "--cd-danger": extras.feedback.danger,
  "--cd-danger-hover": "#dc2626",
  "--cd-focus-ring": extras.shadow.focusRing,
};

export function ConfirmProvider({ children }) {
  const [state, setState] = useState({ open: false, opts: DEFAULTS });
  const resolverRef = useRef(null);
  // The clicked button sets this; ESC / outside-dismiss leaves it false.
  const resultRef = useRef(false);

  const confirm = useCallback(
    (opts = {}) =>
      new Promise((resolve) => {
        resolverRef.current = resolve;
        resultRef.current = false;
        setState({ open: true, opts: { ...DEFAULTS, ...opts } });
      }),
    [],
  );

  // Single close path. Whatever the buttons set in resultRef is the answer;
  // ESC, overlay, or outside-click resolve to false. resolverRef guard makes
  // it impossible to resolve twice regardless of event ordering.
  const handleOpenChange = useCallback((open) => {
    if (open) return;
    setState((s) => ({ ...s, open: false }));
    const resolve = resolverRef.current;
    resolverRef.current = null;
    const result = resultRef.current;
    resultRef.current = false;
    if (resolve) resolve(result);
  }, []);

  const { open, opts } = state;

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AlertDialog.Root open={open} onOpenChange={handleOpenChange}>
        <AlertDialog.Portal>
          <AlertDialog.Overlay className="cd-overlay" style={CSS_VARS} />
          <AlertDialog.Content className="cd-content" style={CSS_VARS}>
            <AlertDialog.Title className="cd-title">
              {opts.title}
            </AlertDialog.Title>
            {opts.message ? (
              <AlertDialog.Description className="cd-description">
                {opts.message}
              </AlertDialog.Description>
            ) : null}
            <div className="cd-actions">
              <AlertDialog.Cancel asChild>
                <button
                  type="button"
                  className="cd-btn cd-btn-cancel"
                  onClick={() => {
                    resultRef.current = false;
                  }}
                >
                  {opts.cancelText}
                </button>
              </AlertDialog.Cancel>
              <AlertDialog.Action asChild>
                <button
                  type="button"
                  className={`cd-btn ${opts.danger ? "cd-btn-danger" : "cd-btn-confirm"}`}
                  onClick={() => {
                    resultRef.current = true;
                  }}
                >
                  {opts.confirmText}
                </button>
              </AlertDialog.Action>
            </div>
          </AlertDialog.Content>
        </AlertDialog.Portal>
      </AlertDialog.Root>
    </ConfirmContext.Provider>
  );
}

/**
 * Returns an async confirm(opts) => Promise<boolean>.
 * Falls back to native window.confirm if no provider is mounted so a flow can
 * never hard-crash on a missing provider.
 */
export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    return (opts = {}) =>
      Promise.resolve(
        window.confirm(opts.message || opts.title || "Are you sure?"),
      );
  }
  return ctx;
}

export default ConfirmProvider;
